'use client';
import PropTypes from 'prop-types';
import { ChartsProvider, ChartsSlotsProvider, defaultSeriesConfig } from '@mui/x-charts/internals';
import type {
  ChartSeriesType,
  ChartAnyPluginSignature,
  ChartsProviderProps,
  ChartSeriesConfig,
} from '@mui/x-charts/internals';
import type { ChartsDataProviderProps } from '@mui/x-charts/ChartsDataProvider';
import { ChartsLocalizationProvider } from '@mui/x-charts/ChartsLocalizationProvider';
import { useLicenseVerifier } from '@mui/x-license/internals';
import { defaultSlotsMaterial } from '../internals/material';
import type { ChartsSlotPropsPro, ChartsSlotsPro } from '../internals/material';
import { DEFAULT_PLUGINS } from '../internals/plugins/allPlugins';
import type { AllPluginSignatures } from '../internals/plugins/allPlugins';
import { useChartsDataProviderProProps } from './useChartsDataProviderProProps';
import { ChartsWatermark } from '../internals/ChartsWatermark';
import { barSampler } from '../internals/plugins/useChartProSampling/barSampler';
import { lineSampler } from '../internals/plugins/useChartProSampling/lineSampler';

const packageInfo = {
  releaseDate: 'MTc4ODM5MzYwMDAwMA==',
  version: process.env.MUI_VERSION!,
  name: 'x-charts-pro' as const,
};

export interface ChartsDataProviderProSlots extends ChartsSlotsPro {}

export interface ChartsDataProviderProSlotProps extends ChartsSlotPropsPro {}

export type ChartsDataProviderProProps<
  SeriesType extends ChartSeriesType = ChartSeriesType,
  TSignatures extends readonly ChartAnyPluginSignature[] = AllPluginSignatures<SeriesType>,
> = ChartsDataProviderProps<SeriesType, TSignatures> &
  ChartsProviderProps<SeriesType, TSignatures>['pluginParams'] & {
    /**
     * Slots to customize charts' components.
     */
    slots?: Partial<ChartsDataProviderProSlots>;
    /**
     * The props for the slots.
     */
    slotProps?: Partial<ChartsDataProviderProSlotProps>;
  };

// Attach the pro sampling strategies (kept at module scope for a stable identity, so the
// sampling-aware axis selectors don't recompute on every render).
export const defaultSeriesConfigPro: ChartSeriesConfig<'bar' | 'scatter' | 'line' | 'pie'> = {
  ...defaultSeriesConfig,
  bar: { ...defaultSeriesConfig.bar, sampler: barSampler },
  line: { ...defaultSeriesConfig.line, sampler: lineSampler },
};

/**
 * Orchestrates the data providers for the chart components and hooks.
 *
 * Use this component if you have custom HTML components that need to access the chart data.
 *
 * Demos:
 *
 * - [Composition](https://mui.com/x/api/charts/composition/)
 *
 * API:
 *
 * - [ChartsDataProviderPro API](https://mui.com/x/api/charts/charts-data-provider-pro/)
 *
 * @example
 * ```jsx
 * <ChartsDataProviderPro
 *   series={[{ label: "Label", type: "bar", data: [10, 20] }]}
 *   xAxis={[{ data: ["A", "B"], scaleType: "band", id: "x-axis" }]}
 * >
 *   <ChartsSurface>
 *      <BarPlot />
 *      <ChartsXAxis axisId="x-axis" />
 *   </ChartsSurface>
 *   {'Custom Legend Component'}
 * </ChartsDataProviderPro>
 * ```
 */
function ChartsDataProviderPro<
  SeriesType extends ChartSeriesType = ChartSeriesType,
  TSignatures extends readonly ChartAnyPluginSignature[] = AllPluginSignatures<SeriesType>,
>(props: ChartsDataProviderProProps<SeriesType, TSignatures>) {
  const { children, localeText, chartProviderProps, slots, slotProps } =
    useChartsDataProviderProProps({
      ...props,
      seriesConfig: props.seriesConfig ?? defaultSeriesConfigPro,
      plugins: props.plugins ?? DEFAULT_PLUGINS,
    });

  useLicenseVerifier(packageInfo);

  return (
    <ChartsProvider {...chartProviderProps}>
      <ChartsLocalizationProvider localeText={localeText}>
        <ChartsSlotsProvider
          slots={slots}
          slotProps={slotProps}
          defaultSlots={defaultSlotsMaterial}
        >
          {children}
        </ChartsSlotsProvider>
      </ChartsLocalizationProvider>
      <ChartsWatermark packageInfo={packageInfo} />
    </ChartsProvider>
  );
}

ChartsDataProviderPro.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  apiRef: PropTypes.shape({
    current: PropTypes.any,
  }),
  /**
   * Color palette used to colorize multiple series.
   * @default rainbowSurgePalette
   */
  colors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.func]),
  /**
   * An array of objects that can be used to populate series and axes data using their `dataKey` property.
   */
  dataset: PropTypes.arrayOf(PropTypes.object),
  /**
   * Options to enable features planned for the next major.
   */
  experimentalFeatures: PropTypes.shape({
    keyboardActivation: PropTypes.bool,
  }),
  /**
   * The height of the chart in px. If not defined, it takes the height of the parent element.
   */
  height: PropTypes.number,
  /**
   * This prop is used to help implement the accessibility logic.
   * If you don't provide this prop. It falls back to a randomly generated id.
   */
  id: PropTypes.string,
  /**
   * Localized text for chart components.
   */
  localeText: PropTypes.object,
  /**
   * The margin between the SVG and the drawing area.
   * It's used for leaving some space for extra information such as the x- and y-axis or legend.
   *
   * Accepts a `number` to be used on all sides or an object with the optional properties: `top`, `bottom`, `left`, and `right`.
   */
  margin: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      bottom: PropTypes.number,
      left: PropTypes.number,
      right: PropTypes.number,
      top: PropTypes.number,
    }),
  ]),
  /**
   * The array of series to display.
   * Each type of series has its own specificity.
   * Please refer to the appropriate docs page to learn more about it.
   */
  series: PropTypes.arrayOf(PropTypes.object),
  /**
   * If `true`, animations are skipped.
   * If unset or `false`, the animations respects the user's `prefers-reduced-motion` setting.
   */
  skipAnimation: PropTypes.bool,
  /**
   * The props for the slots.
   */
  slotProps: PropTypes.object,
  /**
   * Slots to customize charts' components.
   */
  slots: PropTypes.object,
  theme: PropTypes.oneOf(['dark', 'light']),
  /**
   * The width of the chart in px. If not defined, it takes the width of the parent element.
   */
  width: PropTypes.number,
} as any;

export { ChartsDataProviderPro };
